/**
 * Shared prefetch concurrency budget.
 *
 * Rolling-window size: how many image loads we keep in flight at once. We fire
 * up to this many concurrently and start the next as each one settles, rather
 * than loading in fixed batches gated by the slowest member of each batch (the
 * old batch-8 + Promise.allSettled barrier throttled a whole CT series to an
 * effective parallelism of 8, paced by its slowest image). Cornerstone's decode
 * worker pool + HTTP/2 multiplexing absorb this window; it also bounds how much
 * we speculatively fetch, so a hung or slow image only ties up one slot and
 * never stalls the rest. We still yield to user interaction before topping up
 * (waitForUserIdle), so scroll stays responsive. Raising this trades a little
 * speculative-fetch headroom for throughput.
 *
 * Shared by the 2D rolling prefetcher (`PREFETCH_CONFIG.windowSize` in
 * prefetchManager.ts) and the Cornerstone image-load pool's volume-prefetch cap
 * (`configureImageLoadPool` in init.ts), so the two budgets cannot drift apart.
 *
 * This module must not import anything — it exists to break the import cycle
 * between init.ts and prefetchManager.ts.
 */
export const PREFETCH_WINDOW_SIZE = 24;
/**
 * How many cine loops background look-ahead warms beyond the ones on screen.
 *
 * A cine loop is a whole clip of frames, not a slice: one echo clip is ~100
 * colour frames at 1-2 MB each once decoded, so it costs what a hundred-slice
 * CT series does — and an echo study is fifty of them. Warming every clip in
 * proximity order spent the whole image cache on clips the reader may never
 * open, decoding each frame on the main thread to do it (JPEG-baseline colour
 * has no worker decode path). Two covers the clip either side of the focused
 * one, which is where a reader stepping through a study goes next; any other
 * clip loads when it is opened, and the window moves with the focus.
 */
export const LOOKAHEAD_CINE_LOOP_LIMIT = 2;
/**
 * Trim a look-ahead order to at most `limit` cine loops, keeping the ones
 * nearest the focus (the order is already proximity-first — see
 * `computeLookaheadOrder` in prefetchManager.ts). Every series that is not a
 * cine loop keeps its place, so CT, MR, DX and single-frame ultrasound warm
 * exactly as before.
 */
export function limitCineLoopLookahead(lookaheadOrder, isCineLoop, limit = LOOKAHEAD_CINE_LOOP_LIMIT) {
    let cineLoops = 0;
    return lookaheadOrder.filter((id) => {
        if (!isCineLoop(id))
            return true;
        cineLoops++;
        return cineLoops <= limit;
    });
}
